import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles'
import {
  Table,
  TableBody,
  TableContainer,
  TableCell,
  TableRow,
  Paper,
  Checkbox,
  Stack,
  Button,
  IconButton,
  Menu,
  LinearProgress,
  MenuItem, TablePagination, TableFooter
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'

import Emitter from '@utils/eventEmitter'
import {formatUnderFromCamel} from '@utils/formatText'
import Modal from '@views/adminRole/modal/FormModal'
import DynamicHead from '@components/DynamicHead'
import DynamicSearch from '@components/DynamicSearch'

import {
  adminGetGroupList,
  adminGetCompanyList,
  createCompany,
  updateCompany,
  deleteCompany,
  deleteSelectedCompany,
  adminGetCompanyWithExcel,
} from '@store/admin/company'

const ButtonGroup = styled(Stack)({
  padding: '10px 0',
  textAlign: 'right'
})
const OptionsButton = styled(IconButton)({
  padding: 0
})
const ExcelButton = styled(Button)({
  position: 'absolute',
  left: 20
})

const headerList = [
  {key: '', name: '', checkbox: true, width: '5%'},
  {key: 'groupName', name: '대주주', sort: true, search: true, width: '15%'},
  {key: 'companyName', name: '회사명', sort: true, search: true, width: '15%'},
  {key: 'companyIdentify', name: 'ID', sort: true, search: true, width: '15%'},
  {key: 'email', name: '이메일', width: '15%'},
  {key: 'phone', name: '전화번호', width: '15%'},
  {key: 'name', name: '담당자', width: '15%'},
  {key: '', name: '', width: '5%'},
]

const initParams = {
  page: 0,
  rowsPerPage: 10,
  searchType: 'COMPANY_NAME',
  searchKeyword: '',
  sortDirection: null
}

const RoleTable = () => {
  const dispatch = useDispatch()
  const {
    companyList,
    companyListTotalSize
  } = useSelector(store => store.companyAdmin)
  const [ checkeds, setCheckeds ] = useState({})
  const [ modalOpen, setModalOpen ] = useState(false)
  const [ editData, setEditData ] = useState(null)
  const [ params, setParams ] = useState(initParams)
  const [ loading, setLoading ] = useState(false)

  const handleChangePage = (e, newPage) => {
    setParams(Object.assign({}, params, {page: newPage}))
  }

  const handleChangeRowsPerPage = (e) => {
    const currentPageSize = parseInt(e.target.value, 10)
    setParams(Object.assign({}, params, {page: 0, rowsPerPage: currentPageSize}))
  }

  const onSelectAllClick = () => {
    let updateCheckeds = {}
    if (!isAllChecked) {
      companyList.forEach(data => {
        updateCheckeds[data.companyId] = true
      })
    }
    setCheckeds(updateCheckeds)
  }

  const changeChecked = (itemId) => {
    let updateCheckeds = Object.assign({}, checkeds)
    if (checkeds[itemId]) {
      updateCheckeds = Object.assign({}, checkeds)
      delete updateCheckeds[itemId]
    } else {
      updateCheckeds[itemId] = true
    }
    setCheckeds(updateCheckeds)
  }

  const isAllChecked = useMemo(() => {
    if (!companyList.length) return false;
    return Object.keys(checkeds).length === companyList.length
  }, [ checkeds, companyList ])

  const completeModal = async (action, data) => {
    setModalOpen(false)
    if (action) {
      if (data.companyId) {
        dispatch(updateCompany(data))
      } else {
        await dispatch(createCompany(data))
        setParams(Object.assign({}, initParams, {page: 0}))
      }
    } else {
      setEditData(null)
    }
  }

  const openEditModal = (data) => {
    setEditData(data)
    setModalOpen(true)
  }

  const downloadExcels = () => {
    dispatch(adminGetCompanyWithExcel())
  }

  const openDeleteModal = (data) => {
    Emitter.emit('SHOW_CONFIRM', {
      text: '삭제하시겠습니까?',
      successCb: () => {
        dispatch(deleteCompany(data))
      }
    })
  }

  const openSelectedDeleteModal = () => {
    const checkedLength = Object.keys(checkeds).length
    if (!checkedLength) {
      Emitter.emit('SHOW_MESSAGE', {
        text: '선택된 데이터가 없습니다.',
        color: 'error'
      })
      return
    }
    Emitter.emit('SHOW_CONFIRM', {
      text: `${checkedLength}개의 데이터를 삭제하시겠습니까?`,
      successCb: () => {
        const checkedList = Object.keys(checkeds)

        dispatch(deleteSelectedCompany({
          selecteds: checkedList
        }))
      }
    })
  }

  const onChangeSearchType = (value) => {
    setParams(Object.assign({}, params, {
      searchType: value,
      searchKeyword: ''
    }))
  }

  const onChangeSearchKeyword = (value) => {
    setParams(Object.assign({}, params, {
      page: 0,
      searchKeyword: value
    }))
  }

  const changeDirection = (key, value) => {
    const tempDirection = {}
    tempDirection[key] = value
    setParams(Object.assign({}, params, {
      page: 0,
      sortDirection: tempDirection
    }))
  }

  const getFilterSort = useCallback(() => {
    let sort = []
    const {sortDirection} = params;
    if (sortDirection) {
      sort = Object.keys(sortDirection)
        .filter(key => sortDirection[key])
        .map(key => {
          const sortType = formatUnderFromCamel({text: key});
          return `${sortType},${sortDirection[key]}`
        })
    }
    return sort.length ? sort : undefined
  }, [params])

  const onGetCompanyList = useCallback(async () => {
    const {page, rowsPerPage, searchType, searchKeyword} = params
    const resultParams = {
      page,
      size: rowsPerPage,
      searchType: searchKeyword ? searchType : undefined,
      searchKeyword: searchKeyword || undefined,
      sort: getFilterSort(),
    }
    setLoading(true)
    await dispatch(adminGetCompanyList(resultParams))
    setLoading(false)
  }, [dispatch, params, getFilterSort])

  const onGetCompanyGroupList = useCallback(() => {
    dispatch(adminGetGroupList())
  }, [dispatch]) 

  useEffect(() => onGetCompanyList(), [onGetCompanyList])
  useEffect(() => onGetCompanyGroupList(), [onGetCompanyGroupList])

  return (
    <>
      <DynamicSearch 
        list={headerList}
        searchType={params.searchType}
        searchKeyword={params.searchKeyword}
        onChangeSearchKeyword={onChangeSearchKeyword}
        onChangeSearchType={onChangeSearchType}
      />
      <TableContainer component={Paper} sx={{position: 'relative'}}>
        {loading && <LinearProgress sx={{position: 'absolute', width: '100%'}}/>}
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <DynamicHead 
            list={headerList}
            sortDirection={params.sortDirection}
            changeDirection={changeDirection}
            isAllChecked={isAllChecked}
            onSelectAllClick={onSelectAllClick}
          />
          <TableBody>
            {
              companyList.map((item) => (
                <RoleTableItem 
                  item={item} 
                  key={item.companyId}
                  checked={checkeds[item.companyId]}
                  changeChecked={() => changeChecked(item.companyId)}
                  openEditModal={openEditModal}
                  openDeleteModal={openDeleteModal}
                />
              ))
            }
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                count={companyListTotalSize}
                page={params.page}
                rowsPerPage={params.rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 30, 50]}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <ButtonGroup 
        direction="row"
        justifyContent="flex-end"
        spacing={1}
      >
        <ExcelButton variant="contained" disableElevation onClick={downloadExcels}>엑셀 다운로드</ExcelButton>
        <Button variant="contained" disableElevation onClick={() => openEditModal(null)}>등록</Button>
        <Button variant="outlined" onClick={openSelectedDeleteModal}>삭제</Button>
      </ButtonGroup>

      <Modal 
        data={editData}
        open={modalOpen}
        completeModal={completeModal}
      />
    </>
  )
}

const RoleTableItem = ({
  item,
  checked,
  changeChecked,
  openEditModal,
  openDeleteModal
}) => {
  const [ anchorEl, setAnchorEl ] = useState(null)
  const open = !!anchorEl

  const handleShowItems = (e) => {
    e.preventDefault()
    setAnchorEl(e.currentTarget)
  }

  const handleCloseItems = () => {
    setAnchorEl(null)
  }

  const handleOpenEditModal = () => {
    setAnchorEl(null)
    openEditModal(item)
  }

  const handleOpenDeleteConfirm = () => {
    setAnchorEl(null)
    openDeleteModal(item)
  }

  const getMainManager = () => {
    return item.managerList?.filter(manager => manager.state === "MAIN_MANAGER")[0];
  }

  return (
    <TableRow>
      <TableCell padding="checkbox" align="center">
        <Checkbox
          color="primary"
          checked={checked || false}
          onChange={() => changeChecked(item.id)}
        />
      </TableCell>
      <TableCell component="th" scope="row" align="center">{item.groupName}</TableCell>
      <TableCell component="th" scope="row" align="center">
        {item.companyName}
      </TableCell>
      <TableCell align="center">{item.companyIdentify}</TableCell>
      <TableCell align="center">{getMainManager().email}</TableCell>
      <TableCell align="center">{getMainManager().phone}</TableCell>
      <TableCell align="center">{getMainManager().name}</TableCell>
      <TableCell align="center">
        <OptionsButton component="button" className="add-btn" onClick={handleShowItems}>
          <MoreVertIcon />
        </OptionsButton>
        <Menu
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseItems}
        >
          <MenuItem sx={{minWidth: 100}} onClick={handleOpenEditModal}>
            변경
          </MenuItem>
          <MenuItem onClick={handleOpenDeleteConfirm}>
            삭제
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  )
}


export default RoleTable